/**
 * PHASE 8 — object labels on the sky.
 *
 * Pure CPU projection of a small set of named objects (bright stars, deep-sky
 * objects) into screen pixels, using exactly the same MVP as the GPU star
 * pass (buildStarProjection). The DOM layer (main.ts) positions absolutely
 * positioned spans per frame.
 */

import { deepSkyObjects, starCatalog } from "../catalog/catalog";
import type { Quat } from "../orientation/quaternion";
import type { BodyRenderState } from "../render/dynamicBodies";
import { perspective } from "../render/mat4";
import { buildStarProjection, equatorialVector, transformDir } from "../render/skyFrame";

export interface SkyLabel {
  id: string;
  name: string;
  /** J2000 position. */
  raHours: number;
  decDeg: number;
  /** Smaller = brighter; used to scale the text. */
  magnitude: number;
  type: "star" | "deep-sky" | "body";
}

export interface ProjectedLabel {
  id: string;
  name: string;
  xPx: number;
  yPx: number;
  magnitude: number;
  type: "star" | "deep-sky" | "body";
  /** True when the label is in front of the camera (device +z) and on-screen. */
  visible: boolean;
}

/**
 * The labelled subset of the sky: named stars down to the magnitude limit
 * (brightest first, capped) plus all named deep-sky objects.
 */
export function skyLabels(
  starLimitMagnitude = 3,
  maxStars = 40,
  includeDeepSky = true,
): SkyLabel[] {
  const named: SkyLabel[] = [];
  for (const star of starCatalog.brightStars(starLimitMagnitude)) {
    if (!star.name) continue;
    if (star.magnitude < -10) continue; // the Sun (Sol) is not a star-field label
    named.push({
      id: star.hip ? `STAR:${star.hip}` : `STAR:${star.index}`,
      name: star.name,
      raHours: star.raHours,
      decDeg: star.decDegrees,
      magnitude: star.magnitude,
      type: "star",
    });
  }
  named.sort((a, b) => a.magnitude - b.magnitude);
  const out: SkyLabel[] = named.slice(0, maxStars);
  if (includeDeepSky) {
    for (const dso of deepSkyObjects) {
      if (!dso.name) continue;
      out.push({
        id: `DSO:${dso.id}`,
        name: dso.name,
        raHours: dso.ra / 15,
        decDeg: dso.dec,
        magnitude: dso.mag,
        type: "deep-sky",
      });
    }
  }
  return out;
}

/**
 * Labels for the Sun/Moon/planets. Positions come from the SAME cached
 * body render states the GPU layer uses, so the DOM text sits exactly on
 * the drawn sprite.
 */
export function bodyLabels(states: readonly BodyRenderState[]): SkyLabel[] {
  return states.map((state) => ({
    id: state.id,
    name: state.name,
    raHours: state.raHoursJ2000,
    decDeg: state.decDegJ2000,
    magnitude: state.magnitude,
    type: "body",
  }));
}

/**
 * Project labels with the same MVP the GPU uses for stars.
 *
 * @param qCamera world(ENU) -> device, from the fusion service
 * @param fovYDeg vertical field of view in degrees
 * @param aspect device-space width/height
 * @param latitudeDeg observer latitude
 * @param localSiderealHours observer LST (hours)
 * @param j2000ToOfDate precession matrix or null
 * @param widthPx / heightPx canvas size
 * @param labels the labelled subset (see skyLabels)
 */
export function projectLabels(
  qCamera: Quat | null,
  fovYDeg: number,
  aspect: number,
  latitudeDeg: number,
  localSiderealHours: number,
  j2000ToOfDate: { rot: readonly (readonly number[])[] } | null,
  widthPx: number,
  heightPx: number,
  labels: readonly SkyLabel[],
): ProjectedLabel[] {
  const NEAR = 0.1;
  const FAR = 1000;
  const projection = perspective(fovYDeg, aspect, NEAR, FAR);
  const mvp = buildStarProjection(projection, qCamera ?? { x: 0, y: 0, z: 0, w: 1 }, latitudeDeg, localSiderealHours, j2000ToOfDate);
  // transformDir keeps the upper-left 3x3 only, dropping row 3 (which would
  // give w' = -z_view). Recover the view-space z from the clip z:
  //   clip.z = ((far+near)/(near-far)) * z_view = -zScale * z_view
  //   ->  z_view = -clip.z / zScale
  // In front of the camera means z_view in (-far, -near): the camera looks
  // down -z_view, and the sky (device +z) is the -z_view direction.
  const zScale = (FAR + NEAR) / (FAR - NEAR);
  const out: ProjectedLabel[] = [];
  for (const label of labels) {
    const v = equatorialVector(label.raHours, label.decDeg);
    const clip = transformDir(mvp, v);
    const zView = -clip[2] / zScale;
    const inFront = zView < -NEAR && zView > -FAR;
    const ndcX = clip[0] / -zView;
    const ndcY = clip[1] / -zView;
    const xPx = ((ndcX + 1) / 2) * widthPx;
    const yPx = ((1 - ndcY) / 2) * heightPx;
    out.push({
      id: label.id,
      name: label.name,
      xPx,
      yPx,
      magnitude: label.magnitude,
      type: label.type,
      visible: inFront && xPx >= -10 && xPx <= widthPx + 10 && yPx >= -10 && yPx <= heightPx + 10,
    });
  }
  return out;
}